import Promise from 'bluebird';
import WebFont from 'webfontloader';

import { FONT_LOADER_TIMEOUT, GLOBAL_FONT_CSS_URL, TEST } from '../constants';
import API from '../api';
import WatchedContentData from '../features/content/watcher/WatchedContentData';
import { isNotNil, isNil } from '../util/object';
import Watchable from '../model/watcher/Watchable';
import { GlobalContentWatcher } from '../features/content/watcher/ContentWatcher';

const watchable = new Watchable();
GlobalContentWatcher.watch(watchable);

const fontLoadingPromises = new Map();
const loadedFonts = new Set();
const loadedFontCss = new Set();

export const doesFontSeemLoaded = (fontFamily) => {
  if (isNil(fontFamily)) return false;
  // font seems loaded if the w/h is different than text that has no font applied at all
  const testNode = document.createElement('div');
  testNode.style.position = 'absolute';
  testNode.style.visibility = 'hidden';
  testNode.style.fontFamily = 'serif';
  testNode.appendChild(document.createTextNode('This is Snappy!'));
  document.body.appendChild(testNode);
  const { width: w1, height: h1 } = testNode.getBoundingClientRect();
  testNode.style.fontFamily = fontFamily;
  const { width: w2, height: h2 } = testNode.getBoundingClientRect();
  document.body.removeChild(testNode);
  // if dimensions are different, then the fonts are considered loaded
  return w1 !== w2 || h1 !== h2;
};

/**
 * Returns a promise that resolves when the given font family has been loaded
 * by the browser and hence is ready to use, or rejects if the given font family
 * can't be loaded after 10s. Subsequent calls for the a previously successfully
 * loaded font family will resolve immediately, so this call is safe to put in
 * front of any routine that sets the font-family property in the DOM (including
 * text measurements).
 */
export const loadFont = (
  fontObject,
  variants = [],
  organizationId,
  publisherId,
  businessProfileId,
) => {
  const { archived, id, psName: fontFamily } = fontObject;

  const fontCssUrls = [];
  const pendingVariants = new Set(variants);

  // If we haven't loaded the global fonts css file yet, load it.
  if (!loadedFontCss.has(GLOBAL_FONT_CSS_URL)) {
    fontCssUrls.push(GLOBAL_FONT_CSS_URL);
    loadedFontCss.add(GLOBAL_FONT_CSS_URL);
  }

  // If we haven't loaded an org or publishers fonts css file yet, load it.
  if (organizationId && !loadedFontCss.has(organizationId)) {
    fontCssUrls.push(`/api/v1/organizations/${organizationId}/fonts/css`);
    loadedFontCss.add(organizationId);
  }
  if (publisherId && !loadedFontCss.has(publisherId)) {
    fontCssUrls.push(`/api/v1/publishers/${publisherId}/fonts/css`);
    loadedFontCss.add(publisherId);
  }
  if (businessProfileId && !loadedFontCss.has(businessProfileId)) {
    fontCssUrls.push(`/api/v1/snap-pro-profiles/${businessProfileId}/fonts/css`);
    loadedFontCss.add(businessProfileId);
  }

  // load archived fonts a different way
  if (archived && isNotNil(fontFamily)) {
    fontCssUrls.push(`/api/v1/fonts2/css/byName/${fontFamily}`);
    loadedFontCss.add(fontFamily);
  }

  // If we've already loaded this fontFamily, just return the promise from before.
  const fontSeemLoaded = doesFontSeemLoaded(fontFamily);
  if (fontLoadingPromises.has(fontFamily) && fontSeemLoaded) {
    return fontLoadingPromises.get(fontFamily);
  }

  let fontPromise;

  // Use WebFont loading utility to load the font for CSS use.
  const fontCSSPromise = new Promise((resolve, reject) => {
    // Don't try to load fonts in TEST
    // In DEV the css files from the api are empty, we supplement this with an embedded font file locally
    const shouldNotLoadFont = loadedFonts.has(fontFamily) && fontSeemLoaded;
    if (TEST || shouldNotLoadFont) {
      resolve(true);
      return;
    }

    const loaderFamily =
      variants.length > 0 ? `${fontFamily}:${variants.join(',')}` : fontFamily;

    WebFont.load({
      timeout: FONT_LOADER_TIMEOUT,
      classes: false,
      custom: {
        families: [loaderFamily],
        urls: fontCssUrls,
      },
      fontactive(fontName, fvd) {
        if (fontName === fontFamily) {
          if (fvd) {
            pendingVariants.delete(fvd);
          }

          if (pendingVariants.size === 0) {
            loadedFonts.add(fontFamily);
            resolve(true);
          }
        }
      },
      fontinactive(fontName, fvd) {
        if (fontName === fontFamily) {
          reject(new Error(`Unable to load font ${fontName}`));
        }
      },
    });
  });

  // If canvas rendering is enabled, we need to get the binary data for a base64 based URL.
  if (archived) {
    fontPromise = fontCSSPromise.then(() => {
      return API.loadFontByName(fontFamily);
    });
  } else if (isNotNil(id)) {
    // Load font via CSS first then
    // Load font as binary
    // TODO: what about variants?
    const fontBinaryPromise = fontCSSPromise.then(() => {
      return API.loadFontById(id);
    });
    fontPromise = fontBinaryPromise;
  } else {
    fontPromise = fontCSSPromise;
  }

  // Set the promise in the set and return
  fontLoadingPromises.set(fontFamily, fontPromise);
  fontPromise
    .then(() => {
      const data = new WatchedContentData({}, true);
      watchable.notifyWatcher(data);
    })
    .catch((ignored) => {});
  return fontPromise;
};
